import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronDown, CreditCard, RefreshCw } from "lucide-react";
import { apiGet } from "../../utils/api";
import { toast } from "../../utils/toast";
import "./admin-shell.css";

function fmt(v) {
  const t = new Date(v).getTime();
  if (!Number.isFinite(t)) return "-";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(t));
}

const TYPE_LABELS = {
  subscription: "Subscription",
  payment: "Payment",
  invoice: "Invoice",
};

/* ── field configs define which keys to show and how ── */
const SUBSCRIPTION_FIELDS = [
  { key: "id",           label: "Record ID",      mono: true },
  { key: "planName",     label: "Plan Name" },
  { key: "planId",       label: "Plan ID",         mono: true },
  { key: "status",       label: "Status",          badge: true,
    badgeClass: r => r.status === "active" ? "is-active" : r.status === "expired" ? "is-disabled" : "is-pending" },
  { key: "billingCycle", label: "Billing Cycle" },
  { key: "seatCount",    label: "Seats" },
  { key: "paymentMode",  label: "Payment Mode" },
  { key: "notes",        label: "Admin Note" },
  { key: "expiresAt",    label: "Expires At",      date: true },
  { key: "createdAt",    label: "Created At",      date: true },
  { key: "updatedAt",    label: "Updated At",      date: true },
];

const PAYMENT_FIELDS = [
  { key: "id",                  label: "Record ID",       mono: true },
  { key: "planName",            label: "Plan Name" },
  { key: "planId",              label: "Plan ID",          mono: true },
  { key: "totalAmount",         label: "Total Amount",
    format: r => `₹${Number(r.totalAmount || 0).toLocaleString("en-IN")}` },
  { key: "subtotalAmount",      label: "Subtotal",
    format: r => r.subtotalAmount != null ? `₹${Number(r.subtotalAmount).toLocaleString("en-IN")}` : null },
  { key: "taxAmount",           label: "Tax",
    format: r => r.taxAmount != null ? `₹${Number(r.taxAmount).toLocaleString("en-IN")}` : null },
  { key: "status",              label: "Status",           badge: true,
    badgeClass: r => r.status === "paid" ? "is-active" : "is-pending" },
  { key: "paymentMode",         label: "Payment Mode" },
  { key: "seatCount",           label: "Seats" },
  { key: "razorpayPaymentId",   label: "Razorpay Payment ID", mono: true },
  { key: "razorpayOrderId",     label: "Razorpay Order ID",   mono: true },
  { key: "notes",               label: "Admin Note" },
  { key: "createdAt",           label: "Created At",       date: true },
  { key: "updatedAt",           label: "Updated At",       date: true },
];

const INVOICE_FIELDS = [
  { key: "id",            label: "Record ID",     mono: true },
  { key: "invoiceNumber", label: "Invoice #",     mono: true },
  { key: "status",        label: "Status",        badge: true,
    badgeClass: r => r.status === "paid" ? "is-active" : "is-pending" },
  { key: "total",         label: "Total",
    format: r => `₹${Number(r.total || 0).toLocaleString("en-IN")}` },
  { key: "subtotal",      label: "Subtotal",
    format: r => r.subtotal != null ? `₹${Number(r.subtotal).toLocaleString("en-IN")}` : null },
  { key: "tax",           label: "Tax",
    format: r => r.tax != null ? `₹${Number(r.tax).toLocaleString("en-IN")}` : null },
  { key: "billingCycle",  label: "Billing Cycle" },
  { key: "seatCount",     label: "Seats" },
  { key: "notes",         label: "Notes" },
  { key: "dueDate",       label: "Due Date",      date: true },
  { key: "createdAt",     label: "Created At",    date: true },
  { key: "updatedAt",     label: "Updated At",    date: true },
];

const FIELDS_BY_TYPE = {
  subscription: SUBSCRIPTION_FIELDS,
  payment:      PAYMENT_FIELDS,
  invoice:      INVOICE_FIELDS,
};

function DetailField({ field, record }) {
  let value;
  if (field.format) {
    value = field.format(record);
  } else if (field.date) {
    value = record[field.key] ? fmt(record[field.key]) : null;
  } else {
    const raw = record[field.key];
    value = raw != null && raw !== "" ? String(raw) : null;
  }

  if (value == null) return null;

  return (
    <div className="abd-field">
      <span className="abd-field-label">{field.label}</span>
      {field.badge ? (
        <span className={`admin-status ${field.badgeClass(record)}`} style={{ fontSize: "0.78rem" }}>
          {value}
        </span>
      ) : (
        <span className={`abd-field-value${field.mono ? " admin-monospace" : ""}`}>{value}</span>
      )}
    </div>
  );
}

export default function AdminBillingDetail() {
  const { tenantId, billingType, recordId } = useParams();
  const location  = useLocation();
  const navigate  = useNavigate();

  const [record,     setRecord]     = useState(location.state?.record     || null);
  const [tenantName, setTenantName] = useState(location.state?.tenantName || "");
  const [loading,    setLoading]    = useState(!location.state?.record);

  const typeLabel = TYPE_LABELS[billingType] || billingType;
  const fields    = FIELDS_BY_TYPE[billingType] || [];

  const fetchRecord = async () => {
    setLoading(true);
    try {
      const endpoint = billingType === "subscription"
        ? `/api/admin/billing/subscriptions?tenantId=${encodeURIComponent(tenantId)}&limit=100`
        : billingType === "payment"
          ? `/api/admin/billing/payments?tenantId=${encodeURIComponent(tenantId)}&limit=100`
          : `/api/admin/billing/invoices?tenantId=${encodeURIComponent(tenantId)}&limit=100`;

      const listKey = billingType === "subscription" ? "subscriptions"
        : billingType === "payment" ? "payments" : "invoices";

      const res = await apiGet(endpoint);
      const d   = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.message || "Failed to load record");
      const list  = Array.isArray(d?.[listKey]) ? d[listKey] : [];
      const found = list.find(r => r.id === recordId);
      if (found) setRecord(found);
      else { toast.error("Record not found"); navigate(`/admin/tenants/${tenantId}`, { replace: true }); }

      if (!tenantName) {
        const tRes = await apiGet("/api/admin/tenants");
        const tenants = await tRes.json().catch(() => []);
        const t = Array.isArray(tenants) ? tenants.find(t => t.id === tenantId) : null;
        if (t) setTenantName(t.name);
      }
    } catch (e) {
      toast.error(e?.message || "Failed to load record");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!record) void fetchRecord();
  }, [tenantId, billingType, recordId]);

  return (
    <div className="admin-workspace">
      {/* Breadcrumb / back */}
      <div className="abd-breadcrumb">
        <button className="atw-back-btn" onClick={() => navigate("/admin/tenants")}>
          All Tenants
        </button>
        <ChevronDown size={12} style={{ transform: "rotate(-90deg)", color: "var(--as-text-3)" }} />
        <button className="atw-back-btn" onClick={() => navigate(`/admin/tenants/${tenantId}`)}>
          {tenantName || "Tenant"}
        </button>
        <ChevronDown size={12} style={{ transform: "rotate(-90deg)", color: "var(--as-text-3)" }} />
        <button className="atw-back-btn" onClick={() => navigate(`/admin/tenants/${tenantId}`, { state: { tab: "billing" } })}>
          Billing
        </button>
        <ChevronDown size={12} style={{ transform: "rotate(-90deg)", color: "var(--as-text-3)" }} />
        <span className="abd-breadcrumb-current">{typeLabel}</span>
      </div>

      {/* Page header */}
      <div className="abd-page-header">
        <div>
          <h1 className="abd-page-title">
            <CreditCard size={20} />
            {typeLabel} Detail
          </h1>
          {tenantName && (
            <p className="abd-page-sub">{tenantName}</p>
          )}
        </div>
        <button className="gc-icon-btn" data-tip="Refresh" onClick={fetchRecord} disabled={loading}>
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <div className="admin-empty-state">Loading…</div>
      ) : !record ? (
        <div className="admin-empty-state">Record not found.</div>
      ) : (
        <div className="abd-detail-card">
          <div className="abd-fields-grid">
            {fields.map(f => (
              <DetailField key={f.key} field={f} record={record} />
            ))}
          </div>
        </div>
      )}

      <style>{`
        .abd-breadcrumb {
          display: flex; align-items: center; gap: 6px;
          margin-bottom: 16px; flex-wrap: wrap;
        }
        .abd-breadcrumb-current {
          font-size: 13px; color: var(--as-text); font-weight: 500;
        }
        .atw-back-btn {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 13px; font-weight: 500; color: var(--as-text-2);
          background: none; border: none; padding: 0; cursor: pointer;
          transition: color .12s;
        }
        .atw-back-btn:hover { color: var(--as-primary); }

        .abd-page-header {
          display: flex; align-items: flex-start; justify-content: space-between;
          gap: 16px; flex-wrap: wrap; margin-bottom: 20px;
        }
        .abd-page-title {
          display: flex; align-items: center; gap: 10px;
          font-size: 1.35rem; font-weight: 700; color: var(--as-text); margin: 0 0 4px;
        }
        .abd-page-sub { font-size: 13px; color: var(--as-text-2); margin: 0; }

        .abd-detail-card {
          background: var(--as-surface);
          border: 1px solid var(--as-border);
          border-radius: 6px;
          padding: 24px;
          box-shadow: var(--as-shadow);
        }
        .abd-fields-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }
        .abd-field {
          display: flex; flex-direction: column; gap: 4px;
        }
        .abd-field-label {
          font-size: 11px; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.06em; color: var(--as-text-2);
        }
        .abd-field-value {
          font-size: 14px; font-weight: 500; color: var(--as-text);
          word-break: break-all;
        }
      `}</style>
    </div>
  );
}
